import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../api/client';

export interface EventDrilldownDto {
  event: {
    id: string;
    name: string;
    date: string;
    startTime: string;
    endTime: string;
    mealInfo: string | null;
    dressCode: string | null;
    transportInfo: string | null;
    parkingInfo: string | null;
    venue: {
      name: string;
      address: string;
      city: string;
      state: string;
      capacity: number;
      contactName: string | null;
      contactPhone: string | null;
      contactEmail: string | null;
      parkingInfo: string | null;
    };
  };
  vendors: {
    id: string;
    businessName: string;
    contactPerson: string;
    phone: string | null;
    category: string | null;
    boothNumber: string | null;
    zone: string | null;
  }[];
  performers: {
    id: string;
    title: string;
    speaker: string | null;
    startTime: string;
    endTime: string;
    sortOrder: number;
  }[];
  provisioning: {
    registrationCount: number;
    venueCapacity: number;
    capacityUtilizationPercent: number;
    venueName: string;
    venueAddress: string;
    mealBreakdown: { optionName: string; count: number; vipCount: number; patronCount: number; accessibilityCount: number }[] | null;
    guestHighlights: { totalVip: number; totalPatrons: number; totalAccessibility: number };
  };
  sponsors: {
    id: string;
    sponsorName: string;
    contactPerson: string;
    phone: string | null;
    tier: string;
    pledgedAmount: number;
  }[];
  operationsRoles: {
    id: string;
    roleName: string;
    assigneeName: string | null;
    phone: string | null;
    status: string | null;
  }[];
  summary: {
    vendorCount: number;
    performerCount: number;
    registrationCount: number;
    sponsorCount: number;
    checkedInCount: number;
    notCheckedInCount: number;
    totalExpected: number;
  };
  isLegacyBall: boolean;
}

export interface DrilldownError {
  message: string;
  notFound: boolean;
}

interface UseEventDrilldownResult {
  data: EventDrilldownDto | null;
  loading: boolean;
  error: DrilldownError | null;
  retry: () => void;
}

const TIMEOUT_MS = 15_000;

export function useEventDrilldown(eventId: string): UseEventDrilldownResult {
  const [data, setData] = useState<EventDrilldownDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<DrilldownError | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      cleanup();
      setLoading(true);
      setError(null);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      timeoutRef.current = setTimeout(() => {
        controller.abort();
      }, TIMEOUT_MS);

      try {
        const result = await apiClient<EventDrilldownDto>(
          `/logistics-center/event/${eventId}`,
          { signal: controller.signal },
        );
        setData(result);
        setError(null);
      } catch (err: unknown) {
        // Don't update state if aborted due to unmount
        if (controller.signal.aborted && abortControllerRef.current !== controller) {
          return;
        }

        if (err instanceof DOMException && err.name === 'AbortError') {
          // Timeout or manual abort
          setError({ message: 'Request timed out. Please try again.', notFound: false });
        } else if (
          typeof err === 'object' &&
          err !== null &&
          'statusCode' in err
        ) {
          const apiError = err as { statusCode: number; message?: string };
          if (apiError.statusCode === 404) {
            setError({
              message: apiError.message || 'Event not found.',
              notFound: true,
            });
          } else {
            setError({
              message: apiError.message || 'Failed to load event data.',
              notFound: false,
            });
          }
        } else {
          setError({
            message: 'A network error occurred. Please try again.',
            notFound: false,
          });
        }
        setData(null);
      } finally {
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setLoading(false);
      }
    };

    fetchData();

    return cleanup;
  }, [eventId, fetchTrigger, cleanup]);

  const retry = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  return { data, loading, error, retry };
}
